import { useFeatureToggle } from 'app/hooks/useFeatureToggle'
import { useSettings } from 'app/hooks/useSettings'
import { useFeatureToggleStore, useAuthStore } from 'app/stores/StoreContext'
import { useUser, useWorkspace } from 'app/utils'
import { hasNavProduct } from 'lib/sidebarItems'

export const ACTION_CENTER_HREF = `/action-center`
export const HOMEPAGE_FALLBACK_HREF = `/contacts`

/**
 * The pin has been stored in two shapes: org users used to get `{ url, orgId }` and
 * workspace users a plain path. Rows written before this hook still hold the object.
 */
export function storedHomepageHref(value: unknown): string | undefined {
  if (typeof value === `string`) return value || undefined
  if (value && typeof value === `object` && `url` in value) {
    const url = (value as { url?: unknown }).url
    return typeof url === `string` && url ? url : undefined
  }
  return undefined
}

export type Homepage = {
  /** Where `/` resolves: the pinned page, else the Action Center, else contacts. */
  href: string
  /** The user's own pin, absent when the Action Center default is what is in play. */
  explicitHref: string | undefined
  /** True while the Action Center default stands, which is a homepage nobody can unpin. */
  isDefaultHomepage: boolean
  /** Settings and toggles are both in, so `href` is the real answer rather than a guess. */
  loaded: boolean
  setHomepage: (href: string | undefined) => Promise<void>
}

/**
 * Resolves the user's homepage, and owns the single key it is stored under.
 *
 * The key used to be picked with `isOrgUser`, which resolves asynchronously, so a pin
 * could be written under `defaultPage` and read back from `defaultPage-<workspaceId>` on
 * the next load. The pin silently vanished. Keying on `workspaceId` alone, straight off
 * the user object the callers already guard on, leaves nothing to race: a user inside a
 * workspace gets a pin scoped to it, and an org-level user gets the org-wide one.
 */
export function useHomepage(): Homepage {
  const user = useUser()
  const workspace = useWorkspace()
  const { products } = useAuthStore()
  const toggleStore = useFeatureToggleStore()
  const actionCenterEnabled = useFeatureToggle(`action-center`)

  const settingKey = user?.workspaceId ? `defaultPage-${user.workspaceId}` : `defaultPage`
  const [setting, setSetting, settingLoaded] = useSettings(settingKey)

  // The same gate the Action Center nav row passes: its toggle, the CRM product, and a
  // workspace to be in. Reuses hasNavProduct so a loaded, empty product set hides it
  // (TRK-5965), while not-loaded products still allow it (flicker guard).
  const canUseActionCenter = actionCenterEnabled && workspace != null && hasNavProduct(products, `CRM`, true)

  // An explicit pin at the Action Center only counts while the toggle is on. FeatureRoute
  // sends a user without it from /action-center back to `/`, and honoring the pin there
  // would send them straight back again, forever.
  const stored = storedHomepageHref(setting)
  const explicitHref = stored === ACTION_CENTER_HREF && !canUseActionCenter ? undefined : stored

  return {
    href: explicitHref ?? (canUseActionCenter ? ACTION_CENTER_HREF : HOMEPAGE_FALLBACK_HREF),
    explicitHref,
    isDefaultHomepage: explicitHref == null,
    loaded: settingLoaded && toggleStore.loaded,
    setHomepage: async (href: string | undefined) => {
      await setSetting(settingKey, href)
    },
  }
}
